import './accounts.css';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, errorText } from '../../api';
import { fmtDate, initials } from '../../lib/format';
import { useToast } from '../../lib/toast';
import { useBelegeOverview } from '../belege/useBelegeOverview';
import { useKanzlei } from '../context';
import { AREAS, myAreas, USTVA_LIST } from './labels';
import { EndedClients } from './MandatBeenden';
import { RegistrationCard } from './RegistrationCard';
import type { ClientRow, RegistrationRow } from './types';
import { useClientList, useRegistrations } from './useRegistrations';

// Mandantenliste: Admin alle, Mitarbeiter nur zugewiesene. Ganze Zeile öffnet den Mandanten.
export function MandantenList() {
  const { me } = useKanzlei();
  const admin = me.user.isAdmin;
  const navigate = useNavigate();
  const list = useClientList();
  const belege = useBelegeOverview();
  const clients = list.data?.clients ?? [];
  const n = clients.length;

  useEffect(() => {
    document.title = 'Mandanten · Elbcons Kanzlei';
  }, []);

  const subline = admin
    ? n === 1
      ? '1 Mandant'
      : `Alle ${n} Mandanten`
    : n === 1
      ? '1 Mandant, der Ihnen zugewiesen ist'
      : `${n} Mandanten, die Ihnen zugewiesen sind`;

  const person = (p: ClientRow['fibu']) =>
    p ? (
      <>
        <span className="av" aria-hidden="true">
          {initials(p.name)}
        </span>
        {p.name}
      </>
    ) : (
      <span className="pill p-bad">offen</span>
    );

  return (
    <div className="acc">
      <div className="head">
        <div>
          <h1>Mandanten</h1>
          <p>{list.data ? subline : ' '}</p>
        </div>
      </div>
      {admin && <RegistrationCard />}
      <div className="card">
        {!list.data ? (
          list.error ? (
            <p className="note">{list.error}</p>
          ) : (
            <div className="spinner" aria-label="Lädt" />
          )
        ) : n === 0 ? (
          <p className="note empty-note">
            {admin
              ? 'Noch keine Mandanten. Neue Mandanten erscheinen hier nach der Freigabe.'
              : 'Ihnen ist noch kein Mandant zugewiesen.'}
          </p>
        ) : (
          <div className="tw">
            <table className="tmand">
              <thead>
                <tr>
                  <th>Mandant</th>
                  <th>Ansprechpartner</th>
                  {admin ? (
                    <>
                      <th>Finanzbuchhaltung</th>
                      <th>Lohn</th>
                    </>
                  ) : (
                    <th>Ihr Bereich</th>
                  )}
                  <th>Umsatzsteuer</th>
                  <th>Belege</th>
                  <th>Mitarbeiter</th>
                </tr>
              </thead>
              <tbody>
                {clients.map((c) => {
                  const b = belege[c.id];
                  return (
                    <tr key={c.id} className="click" onClick={() => navigate(`/kanzlei/mandanten/${c.id}`)}>
                      <td>
                        {c.country === 'AE' && <span className="flag f-ae">UAE</span>}
                        <Link className="rowlink" to={`/kanzlei/mandanten/${c.id}`} onClick={(e) => e.stopPropagation()}>
                          {c.name}
                        </Link>
                        <span className="sub">Nr. {c.number}</span>
                      </td>
                      <td data-l="Ansprechpartner">{c.contactName}</td>
                      {admin ? (
                        AREAS.map((a) => (
                          <td key={a.key} data-l={a.label}>
                            {person(c[a.key])}
                          </td>
                        ))
                      ) : (
                        <td data-l="Ihr Bereich">
                          <span className="pill p-info">{myAreas(c, me.user.id)}</span>
                        </td>
                      )}
                      <td data-l="Umsatzsteuer">{c.country === 'AE' ? 'VAT quartalsweise' : USTVA_LIST[c.ustva]}</td>
                      <td data-l="Belege">{b ? <span className={`pill p-${b.tone === 'none' ? 'neu' : b.tone}`}>{b.label}</span> : '–'}</td>
                      <td className="num" data-l="Mitarbeiter">
                        {c.employees ?? '–'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {admin && <RejectedRegistrations />}
      {admin && <EndedClients />}
    </div>
  );
}

// Abgelehnte Registrierungen (Admin), eingeklappt. „Wieder öffnen“ führt zur Prüfung.
function RejectedRegistrations() {
  const regs = useRegistrations();
  const navigate = useNavigate();
  const toast = useToast();
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const rejected = regs.data?.rejected ?? [];
  if (!rejected.length) return null;

  async function reopen(g: RegistrationRow) {
    setBusy(g.id);
    try {
      await api.post(`/api/kanzlei/registrations/${g.id}/reopen`);
      toast(`${g.name} wartet wieder auf Freigabe`);
      navigate(`/kanzlei/registrierungen/${g.id}`);
    } catch (err) {
      toast(errorText(err));
      setBusy(null);
    }
  }

  return (
    <div className="acc rejected">
      <button type="button" className="btn sm" aria-expanded={show} aria-controls="rej-list" onClick={() => setShow((s) => !s)}>
        {show ? 'Abgelehnte Registrierungen ausblenden' : `Abgelehnte Registrierungen (${rejected.length})`}
        <span aria-hidden="true">{show ? ' ▴' : ' ▾'}</span>
      </button>
      {show && (
        <div className="card" id="rej-list">
          {rejected.map((g) => (
            <div className="task" key={g.id}>
              <span className="av" aria-hidden="true">
                {initials(g.name)}
              </span>
              <div className="tx">
                <b>{g.name}</b>
                <small>
                  {g.contactName} · registriert am {fmtDate(g.registeredAt)}
                  {g.rejectReason ? ` · Grund: ${g.rejectReason}` : ''}
                </small>
              </div>
              <button
                type="button"
                className="btn sm"
                onClick={() => void reopen(g)}
                disabled={busy === g.id}
                aria-label={`${g.name} wieder öffnen`}
              >
                Wieder öffnen
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
